// ─────────────────────────────────────────────
// analytics/sessionTracker.js
// AtlasRouteEQ™ — Session Lifecycle Tracking
//
// Tracks user journey sessions so events can be
// grouped, timed, and analyzed for abandonment.
//
// NEW CODE — Commercial Use Review:
//   - No third-party libraries
//   - Built-in browser APIs only (sessionStorage)
//   - All logic is original IP ✅
//
// License: MIT (your original IP)
// ─────────────────────────────────────────────

import { supabase } from "../services/supabaseService";

const SESSION_KEY  = "atlasrouteeq_session_id";
const SESSION_META = "atlasrouteeq_session_meta";

/**
 * Get or create a session ID.
 * A session represents one continuous visit.
 * Cleared when the browser tab closes (sessionStorage).
 */
export function getSessionId() {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem(SESSION_KEY, id);
    sessionStorage.setItem(SESSION_META, JSON.stringify({
      started_at: new Date().toISOString(),
      page_views: 0,
      booking_started: false,
      booking_completed: false,
      furthest_step: 0,
    }));
  }
  return id;
}

/**
 * Update in-memory session metadata.
 * This persists within the tab, not to the server.
 */
export function updateSessionMeta(updates) {
  try {
    const meta = getSessionMeta();
    sessionStorage.setItem(SESSION_META, JSON.stringify({ ...meta, ...updates }));
  } catch (e) {}
}

/** Read current session metadata */
export function getSessionMeta() {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_META) || "{}");
  } catch (e) {
    return {};
  }
}

/**
 * Track how long a user spent on a booking step.
 * Call startStepTimer() on enter, stopStepTimer() on exit.
 */
const stepTimers = {};

export function startStepTimer(step) {
  stepTimers[step] = Date.now();
}

export function stopStepTimer(step) {
  const start = stepTimers[step];
  if (!start) return null;
  const durationMs = Date.now() - start;
  delete stepTimers[step];
  return durationMs;
}

/**
 * Record step duration to session metadata.
 * Tracks which step the user spent the most time on (friction signal).
 */
export function recordStepDuration(step, durationMs) {
  const meta = getSessionMeta();
  const stepDurations = meta.step_durations || {};
  stepDurations[`step_${step}`] = durationMs;
  const furthestStep = Math.max(meta.furthest_step || 0, step);
  updateSessionMeta({ step_durations: stepDurations, furthest_step: furthestStep });
}

/**
 * Mark booking as started in session metadata.
 */
export function markBookingStarted() {
  updateSessionMeta({ booking_started: true, booking_started_at: new Date().toISOString() });
}

/**
 * Mark booking as completed in session metadata.
 */
export function markBookingCompleted() {
  updateSessionMeta({ booking_completed: true, booking_completed_at: new Date().toISOString() });
}

/**
 * Detect session abandonment:
 * booking was started but not completed.
 */
export function wasSessionAbandoned() {
  const meta = getSessionMeta();
  return meta.booking_started && !meta.booking_completed;
}

/**
 * Flush final session summary to Supabase when the user leaves.
 * Call this on window.beforeunload.
 *
 * Uses navigator.sendBeacon for reliability on page unload.
 * sendBeacon is a browser built-in — no external dependency.
 */
export function flushSession(userId) {
  try {
    const meta     = getSessionMeta();
    const sessionId = getSessionId();
    const summary  = {
      session_id:          sessionId,
      user_id:             userId,
      started_at:          meta.started_at,
      ended_at:            new Date().toISOString(),
      furthest_step:       meta.furthest_step || 0,
      booking_started:     meta.booking_started || false,
      booking_completed:   meta.booking_completed || false,
      abandoned:           wasSessionAbandoned(),
      step_durations:      meta.step_durations || {},
    };
    // Use sendBeacon so the request fires even as the page unloads
    const SUPABASE_URL     = process.env.REACT_APP_SUPABASE_URL;
    const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
    const blob = new Blob([JSON.stringify(summary)], { type: "application/json" });
    navigator.sendBeacon(
      `${SUPABASE_URL}/rest/v1/session_summaries`,
      blob
    );
  } catch (e) {
    // Non-fatal
  }
}

/**
 * Get the total elapsed time for the current booking session (ms).
 */
export function getBookingElapsedMs() {
  const meta = getSessionMeta();
  if (!meta.booking_started_at) return null;
  return Date.now() - new Date(meta.booking_started_at).getTime();
}
